/**
 * Preset Apps — tiles My Apps shows without the account owning a
 * ComputeInstance for them.
 *
 * My Apps is otherwise entirely instance-backed: every tile in
 * `computeClient.list()` is one account's one ComputeInstance. Playground
 * breaks that on purpose — it is ONE global Lobby every user opens, so a
 * per-user instance would be a meaningless row invented to satisfy a URL.
 * This is the smallest boundary that lets a tile exist without one.
 *
 * The tile now points at Playground's own canonical app host and opens the
 * way every other App tile opens (`appLinkProps` — new tab), because
 * Playground IS a normal app as far as My Apps is concerned. It briefly went
 * through in-app SPA navigation while Playground was a PWA route; that is no
 * longer where Playground lives.
 *
 * Checked for reuse first: there is no existing preset/system-app mechanism
 * in this codebase (grepped for "preset" and "SystemApp" — no hits).
 */
import { playgroundCanonicalUrl } from "@/playground/playgroundLocation";
import { readDismissedPresets } from "@/components/personal-apps/presetDismissal";

export interface PresetAppDefinition {
  id: string;
  display_name: string;
  /** Absolute URL on the preset's own app host. */
  url: string;
  /**
   * Whether the tile can be taken off the home screen.
   *
   * `true` for Plaza, but it does NOT mean what it means for an App: there is
   * no instance and no data to delete, so removing only stops showing the
   * tile and Discover puts it back. See `presetDismissal.ts`.
   */
  removable: boolean;
  icon_color: string;
  /** A single rendered glyph (emoji), not an icon name — see AppIcon. */
  icon_glyph: string;
  /**
   * Artwork on this origin's own public asset path.
   *
   * Passed to `AppIcon` as `listingIconUrl`, not `imageUrl`: `imageUrl` is the
   * session-authorized personal-icon endpoint and requests it with
   * `crossOrigin="use-credentials"`, which a plain static file does not need
   * and would only make fail. The colour and glyph below stay as the fallback
   * for when the file 404s — `AppIcon` falls through to the drawn icon rather
   * than leaving a broken image in the grid.
   */
  icon_url?: string;
}

/**
 * Every preset this build defines, regardless of whether somebody has taken
 * it off their home screen.
 *
 * Separate from `presetApps()` because Discover needs to see a DISMISSED
 * preset in order to offer it back. Filtering here would make "add it again
 * from Discover" impossible, which is the promise the removal makes.
 *
 * Fails closed on the canonical host: without an origin configured a tile
 * would link nowhere — worse than no tile.
 */
export function definedPresetApps(): PresetAppDefinition[] {
  const url = playgroundCanonicalUrl();
  if (!url) return [];
  return [
    {
      // The id is deliberately NOT renamed with the product name. It is the
      // tile's stable identity, and the route, canonical host and the API's
      // reserved slug are all still `playground` — renaming only the visible
      // half keeps this one rename from turning into an infrastructure change.
      id: "ato.playground",
      display_name: "Plaza",
      url,
      removable: true,
      icon_color: "violet",
      icon_glyph: "✨",
      icon_url: "/icons/plaza-icon.png",
    },
  ];
}

/**
 * The presets to actually draw on My Apps: everything this build defines,
 * minus whatever this person has taken off their screen.
 *
 * Takes the dismissed list as an argument so a component can hold it in
 * state and re-render when it changes; the default reads storage for callers
 * that just want the current answer.
 */
export function presetApps(
  dismissed: readonly string[] = readDismissedPresets(),
): PresetAppDefinition[] {
  const hidden = new Set(dismissed);
  return definedPresetApps().filter((preset) => !hidden.has(preset.id));
}

/**
 * The same presets, shaped for the Discover grid.
 *
 * Deliberately built on the client from `presetApps()` rather than served by
 * `/v1/discover/apps`. Discover is a catalogue of Capsules, and adding one
 * creates a ComputeInstance for the person who added it. Plaza has neither:
 * it is ONE global Lobby, so a per-user instance would be a row in the
 * database that means nothing, and a catalogue entry would be a Capsule that
 * does not exist. Inventing either to get a card on this screen is the thing
 * this boundary exists to avoid.
 *
 * `added` is the honest state rather than a constant: a preset that is still
 * on the home screen renders "Open", and one that has been taken off renders
 * "Add", because being able to put it back is the whole reason removing it is
 * allowed to be non-destructive.
 */
export function presetDiscoverApps(
  dismissed: readonly string[] = readDismissedPresets(),
): PresetDiscoverApp[] {
  const hidden = new Set(dismissed);
  return definedPresetApps().map((preset) => ({
    id: preset.id,
    preset: true as const,
    title: preset.display_name,
    description:
      "A shared square where everyone gathers. Walk around, talk, and try what you find.",
    app_url: preset.url,
    icon: preset.icon_url ?? null,
    thumbnail_url: preset.icon_url ?? null,
    added: !hidden.has(preset.id),
  }));
}

export interface PresetDiscoverApp {
  id: string;
  /** Marks the card as preset-backed: dismissed and restored, never installed. */
  preset: true;
  title: string;
  description: string;
  app_url: string;
  icon: string | null;
  thumbnail_url: string | null;
  /** On the home screen already (Open), or taken off it (Add). */
  added: boolean;
}
